import Logo from "../assets/RCS-Logo-White.png";
import {
  getLiquidationSettlementAmount,
  getLiquidationSettlementLabel,
} from "./liquidationSettlement";

const money = (value, locale = "en-US") =>
  Number(value || 0).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "https://track-trace-server.onrender.com"
    : "http://localhost:5000";

const resolveSignatureUrl = (value) => {
  const url = String(value || "").trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE_URL.replace(/\/$/, "")}/${url.replace(/^\/+/, "")}`;
};

export const buildRequestVoucherHtml = ({ item, shipment }) => {
  // Get request-level currency and exchange rate
  const requestCurrency = String(item?.currency || "PHP").toUpperCase();
  const requestExchangeRate = Number(item?.exchange_rate || 1);
  const isUsd = requestCurrency === "USD";
  const currencySymbol = isUsd ? "$" : "₱";

  const particularsHtml = (item?.particulars || [])
    .map((p) => {
      // p.total and p.amount are stored in the original currency (USD if USD, PHP if PHP)
      const totalOriginal = Number(p.total || 0);
      const unitCostOriginal = Number(p.amount || 0);
      // Convert to PHP for display if USD
      const totalPhp = isUsd ? totalOriginal * requestExchangeRate : totalOriginal;
      // Compute tax as 12% of total (total includes tax, so: tax = total / 1.12 * 0.12)
      const taxAmountPhp = p.with_tax ? (totalPhp / 1.12 * 0.12) : 0;
      const netAmountPhp = totalPhp - taxAmountPhp;
      // Calculate USD equivalents for tax and net (divide PHP amounts by exchange rate)
      const taxAmountUsd = isUsd && requestExchangeRate > 0 ? taxAmountPhp / requestExchangeRate : 0;
      const netAmountUsd = isUsd && requestExchangeRate > 0 ? netAmountPhp / requestExchangeRate : 0;
      // Show currency and conversion rate info if USD is used (same style as Qty/Unit Cost line)
      const currencyInfo = isUsd
        ? `<div><strong>Currency:</strong> ${requestCurrency} &nbsp; <strong>Conversion Rate:</strong> ${requestExchangeRate.toFixed(4)}</div>`
        : "";
      // For NET AMOUNT column: show dual amounts if USD
      const netDisplay = isUsd
        ? `<div>${currencySymbol}${money(netAmountUsd, "en-PH")}</div><div style="font-size:11px;color:#555;">₱${money(netAmountPhp, "en-PH")}</div>`
        : `₱${money(netAmountPhp, "en-PH")}`;
      // For TAX column: show dual amounts if USD
      const taxDisplay = isUsd
        ? `<div>${currencySymbol}${money(taxAmountUsd, "en-PH")}</div><div style="font-size:11px;color:#555;">₱${money(taxAmountPhp, "en-PH")}</div>`
        : `₱${money(taxAmountPhp, "en-PH")}`;
      // For TOTAL column: show dual amounts if USD
      const totalDisplay = isUsd
        ? `<div>${currencySymbol}${money(totalOriginal, "en-PH")}</div><div style="font-size:11px;color:#555;">₱${money(totalPhp, "en-PH")}</div>`
        : `₱${money(totalPhp, "en-PH")}`;
      return `<tr>
          <td style="padding:6px 8px; border:1px solid #000;">
            <div>${p.description || ""}</div>
            <div><strong>Qty:</strong> ${p.quantity || ""} &nbsp; <strong>Unit Cost:</strong> ${currencySymbol}${money(unitCostOriginal, "en-PH")}</div>
            ${currencyInfo}
          </td>
          <td style="padding:6px 8px; border:1px solid #000; text-align:right;">${p.with_tax ? netDisplay : "—"}</td>
          <td style="padding:6px 8px; border:1px solid #000; text-align:right;">${p.with_tax ? taxDisplay : "—"}</td>
          <td style="padding:6px 8px; border:1px solid #000; text-align:right; font-weight:600;">${totalDisplay}</td>
        </tr>`;
    })
    .join("");

  return `
    <html><head><title>Cash Voucher ${item?.rfp_no || ""}</title>
    <style>
      *{box-sizing:border-box;} body{font-family:"Times New Roman",serif;color:#000;margin:18px;}
      h1{text-align:center;letter-spacing:2px;margin:0 0 4px;}
      .subhead{text-align:center;font-weight:600;margin:0 0 12px;}
      .row{display:flex;justify-content:space-between;gap:12px;margin-bottom:6px;}
      .block{flex:1;}
      .label-line{display:flex;gap:6px;align-items:center;margin-bottom:6px;}
      table{width:100%;border-collapse:collapse;margin-top:8px;}
      th,td{border:1px solid #000;padding:6px 8px;vertical-align:top;}
      th{text-align:left;}
      .tot{margin-top:6px;display:flex;justify-content:flex-end;}
      .tot .box{border:1px solid #000;padding:6px 12px;min-width:220px;}
      .sig-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-top:14px;}
      .sig{text-align:center;min-height:110px;display:flex;flex-direction:column;justify-content:flex-end;gap:6px;}
      .sig img{max-height:52px;object-fit:contain;margin:0 auto;}
      .sig-line{border-top:1px solid #000;height:1px;margin:6px 0;}
      .note{font-size:12px;margin-top:4px;}
    </style></head><body>
      <h1>CASH VOUCHER</h1>
      <div class="subhead">RFP No: ${item?.rfp_no || ""}</div>
      <div style="margin-bottom:10px;">
        <div><strong>Date:</strong> ${item?.created_at ? new Date(item.created_at).toLocaleDateString() : ""}</div>
        <div><strong>Payee:</strong> ${item?.payee || ""}</div>
        <div><strong>Job Order:</strong> ${item?.job_order || ""}</div>
        <div><strong>Corporate Client:</strong> ${shipment?.corporate_client || ""}</div>
        <div><strong>Shipment Description:</strong> ${shipment?.shipment_description || ""}</div>
      </div>

      <table>
        <thead>
          <tr>
            <th style="width:40%">PARTICULARS<br><small>Reference / Description / Quantity / Unit cost</small></th>
            <th style="width:15%; text-align:right;">NET AMOUNT</th>
            <th style="width:15%; text-align:right;">TAX (12%)</th>
            <th style="width:15%; text-align:right;">TOTAL</th>
          </tr>
        </thead>
        <tbody>
          ${particularsHtml}
          <tr><td style="height:80px;"></td><td></td><td></td><td></td></tr>
        </tbody>
      </table>
      <div class="tot">
        <div class="box">
          ${isUsd ? `
            <div style="display:flex;justify-content:space-between;margin-bottom:4px;">
              <span><strong>TOTAL USD</strong></span>
              <span><small>USD</small> $${money(item?.total_amount / requestExchangeRate, "en-PH")}</span>
            </div>
            <div style="display:flex;justify-content:space-between;">
              <span></span>
              <span><small>PHP</small> ₱${money(item?.total_amount, "en-PH")}</span>
            </div>
          ` : `<strong>TOTAL ₱</strong> ${money(item?.total_amount, "en-PH")}`}
        </div>
      </div>

      <div style="margin-top:10px;">
        <div><strong>Received from:</strong> _______________________</div>
        <div><strong>The amount of PESOS:</strong> __________________</div>
        <div class="note">In full payment of amount described above.</div>
      </div>

      <div class="sig-grid">
        <div class="sig">
          ${resolveSignatureUrl(item?.preparer_signature_url) ? `<img src="${resolveSignatureUrl(item.preparer_signature_url)}" alt="Preparer signature">` : "<div style='height:52px'></div>"}
          <div class="sig-line"></div>
          <div>Prepared by: ${item?.preparer_name || "____________________"}</div>
        </div>
        <div class="sig">
          ${resolveSignatureUrl(item?.approver_signature_url) ? `<img src="${resolveSignatureUrl(item.approver_signature_url)}" alt="Approver signature">` : "<div style='height:52px'></div>"}
          <div class="sig-line"></div>
          <div>Approved by (Final): ${item?.approver_name || "____________________"}</div>
        </div>
        <div class="sig">
          ${resolveSignatureUrl(item?.receiver_signature_url) ? `<img src="${resolveSignatureUrl(item.receiver_signature_url)}" alt="Approved/Received signature">` : "<div style='height:52px'></div>"}
          <div class="sig-line"></div>
          <div>Approved/Received by: ${item?.receiver_name || "____________________"}</div>
        </div>
      </div>
    </body></html>
  `;
};

export const buildLiquidationDocumentHtml = ({
  request,
  reportData,
  shipment,
  reportDateCreatedValue,
  dockValue,
  poNoValue,
  referenceNoValue,
  deliveryDateValue,
  actualRows = [],
  advanceRowsValue = [],
  actualCostTotalValue = 0,
  advancesTotalValue = 0,
  reimbursementTotalValue = 0,
}) => `
  <html>
    <head>
      <title>Liquidation Report ${request?.rfp_no || ""}</title>
      <style>
        *{box-sizing:border-box;}
        body{font-family:"Times New Roman",serif;color:#111;margin:0;background:#fff;}
        .page{max-width:1100px;margin:0 auto;padding:22px 28px 32px;}
        .brand{display:flex;align-items:center;justify-content:center;padding:8px 0 16px;}
        .brand img{height:58px;object-fit:contain;}
        h1{text-align:center;margin:0;font-size:28px;letter-spacing:1px;}
        h2{text-align:center;margin:4px 0 0;font-size:18px;font-weight:700;}
        .meta{margin-top:18px;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px 24px;font-size:13px;}
        .meta .full{grid-column:1 / -1;}
        .section-title{margin-top:18px;background:#1f3b64;color:#fff;padding:8px 12px;font-size:14px;font-weight:700;letter-spacing:.4px;}
        table{width:100%;border-collapse:collapse;margin-top:10px;font-size:13px;}
        th,td{border:1px solid #111;padding:7px 8px;vertical-align:top;}
        th{background:#f3f5f8;text-align:left;}
        .right{text-align:right;}
        .totals{margin-top:8px;display:flex;justify-content:flex-end;}
        .totals .box{min-width:320px;border:1px solid #111;padding:10px 12px;font-weight:700;}
        .signatures{margin-top:40px;display:grid;grid-template-columns:repeat(2,1fr);gap:24px;}
        .sig-box{text-align:center;padding:12px;}
        .sig-label{font-size:12px;color:#555;margin-bottom:8px;}
        .sig-image{height:60px;display:flex;align-items:center;justify-content:center;margin-bottom:4px;}
        .sig-image img{max-height:60px;max-width:150px;object-fit:contain;}
        .sig-name{font-weight:700;font-size:14px;border-top:1px solid #111;padding-top:8px;min-height:24px;}
        .sig-title{font-size:12px;color:#555;margin-top:2px;}
      </style>
    </head>
    <body>
      <div class="page">
        <div class="brand"><img src="${Logo}" alt="RCS Logo"></div>
        <h1>Liquidation Report</h1>
        <h2>BROKERAGE DEPARTMENT LIQUIDATION REPORT</h2>

        <div class="meta">
          <div><strong>Date:</strong> ${reportDateCreatedValue ? new Date(reportDateCreatedValue).toLocaleDateString() : "—"}</div>
          <div><strong>Consignee:</strong> ${shipment?.receiver_name || reportData?.consignee || "—"}</div>
          <div><strong>JO:</strong> ${request?.job_order || reportData?.job_order || "—"}</div>
          <div><strong>Tracking No:</strong> ${shipment?.tracking_no || reportData?.tracking_no || "—"}</div>
          <div class="full"><strong>Shipment Details:</strong> ${shipment?.shipment_description || reportData?.shipment_details || "—"}</div>
          <div><strong>Dock:</strong> ${dockValue || "—"}</div>
          <div><strong>P.O No:</strong> ${poNoValue || "—"}</div>
          <div><strong>Reference No:</strong> ${referenceNoValue || "—"}</div>
          <div><strong>Delivery Date:</strong> ${deliveryDateValue || "—"}</div>
        </div>

        <div class="section-title">COST INCURRED FOR SHIPMENT</div>
        <table>
          <thead>
            <tr>
              <th>Particulars</th>
              <th>OR No.</th>
              <th class="right">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${actualRows
              .map(
                (row) => `
                  <tr>
                    <td>${row.particulars || "—"}</td>
                    <td>${row.or_no || "—"}</td>
                    <td class="right">${money(row.amount, "en-US")}</td>
                  </tr>`
              )
              .join("")}
          </tbody>
        </table>
        <div class="totals"><div class="box">TOTAL ACTUAL COST INCURRED: ₱${money(actualCostTotalValue, "en-PH")}</div></div>

        <div class="section-title">ADVANCES TABLE</div>
        <table>
          <thead>
            <tr>
              <th>Particulars</th>
              <th>OR No.</th>
              <th class="right">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${advanceRowsValue
              .map(
                (row) => `
                  <tr>
                    <td>${row.particulars || "—"}</td>
                    <td>${row.or_no || "—"}</td>
                    <td class="right">${money(row.amount, "en-US")}</td>
                  </tr>`
              )
              .join("")}
          </tbody>
        </table>
        <div class="totals"><div class="box">TOTAL ADVANCES: ₱${money(advancesTotalValue, "en-PH")}</div></div>

        <div class="totals" style="margin-top:14px;">
          <div class="box">LIQUIDATION: ${getLiquidationSettlementLabel(reimbursementTotalValue)} ₱${money(getLiquidationSettlementAmount(reimbursementTotalValue), "en-PH")}</div>
        </div>

        <div class="signatures">
          <div class="sig-box">
            <div class="sig-label">Prepared by:</div>
            <div class="sig-image">
              ${resolveSignatureUrl(reportData?.preparer_signature_url) ? `<img src="${resolveSignatureUrl(reportData.preparer_signature_url)}" alt="Preparer Signature" />` : ""}
            </div>
            <div class="sig-name">${reportData?.preparer_name || reportData?.created_by_name || request?.preparer_name || "—"}</div>
            <div class="sig-title">Preparer</div>
          </div>
          <div class="sig-box">
            <div class="sig-label">Noted by:</div>
            <div class="sig-image">
              ${resolveSignatureUrl(reportData?.team_leader_approved_signature_url) ? `<img src="${resolveSignatureUrl(reportData.team_leader_approved_signature_url)}" alt="Team Leader Signature" />` : ""}
            </div>
            <div class="sig-name">${reportData?.team_leader_approved_name || reportData?.team_leader_name || request?.team_leader_name || "—"}</div>
            <div class="sig-title">Brokerage Team Leader</div>
          </div>
        </div>
      </div>
    </body>
  </html>
`;
